import {makeAutoObservable, observable} from 'mobx';

import {User} from '@/domain';
import {Match, matchSortByFavouriteAt} from '@/domain/models/Match';
import {MatchMessageType} from '@/domain/models/MatchMessage';
import {sortByCreatedAtDesc} from '@/domain/models/Model';
import {isNonNullable} from '@/shared/lib/boolean/isNonNullable';

import {favoritedStore} from './favorited';
import type {LastRowViewport, LastViewport} from './types';

function hasVerificationLastMessage(match: Pick<Match, 'last_messages'>) {
  return (
    match.last_messages?.some((message) => {
      switch (message.type) {
        case MatchMessageType.photoVerificationRequest:
        case MatchMessageType.photoVerificationComplete:
        case MatchMessageType.locationVerificationRequest:
        case MatchMessageType.locationVerificationComplete:
          return true;
        default:
          return false;
      }
    }) ?? false
  );
}

class MatchStore {
  readonly matches = observable.map<Match['id'], Omit<Match, 'last_messages'>>(
    undefined
  );

  activeMatchId: Match['id'] | null = null;
  favorited = favoritedStore;
  isInitialLoaded = false;
  fetchedAllOpen = false;
  fetchedAllNew = false;
  lastLoadedOpenAt = {
    lastAt: 0,
    favouriteAt: 0,
  };
  lastLoadedNewAt = 0;

  private lastOpenViewport: LastViewport | undefined;
  private lastNewViewport: LastRowViewport | undefined;

  constructor() {
    makeAutoObservable<MatchStore, 'lastOpenViewport' | 'lastNewViewport'>(
      this,
      {
        lastOpenViewport: false,
        lastNewViewport: false,
      }
    );
  }

  get openedMatchesIds() {
    return [...this.matches.values()]
      .filter((match) => match.status !== 'NEW')
      .sort(matchSortByFavouriteAt)
      .map((match) => match.id);
  }

  get newMatchesIds() {
    return [...this.matches.values()]
      .filter((match) => match.status === 'NEW')
      .sort(sortByCreatedAtDesc)
      .map((match) => match.id);
  }

  get openMatches(): Match[] {
    return this.openedMatchesIds
      .map((matchId) => this.matches.get(matchId))
      .filter(isNonNullable);
  }

  get newMatches(): Match[] {
    return this.newMatchesIds
      .map((matchId) => this.matches.get(matchId))
      .filter(isNonNullable);
  }

  get empty(): boolean {
    return (
      this.newMatchesIds.length === 0 && this.openedMatchesIds.length === 0
    );
  }

  setInitialLoaded(value: boolean) {
    this.isInitialLoaded = value;
  }

  setFetchedAllOpen(value: boolean) {
    this.fetchedAllOpen = value;
  }

  setFetchedAllNew(value: boolean) {
    this.fetchedAllNew = value;
  }

  setActiveMatchId(matchId: Match['id'] | null) {
    this.activeMatchId = matchId;
  }

  private normalizeMatch(match: Match): Match {
    if (match.status === 'NEW' && hasVerificationLastMessage(match)) {
      return {
        ...match,
        status: 'OPEN',
      };
    }

    return match;
  }

  private addMatches(matches: Match[]) {
    this.matches.merge(
      matches.map((match) => {
        const normalizedMatch = this.normalizeMatch(match);

        return [normalizedMatch.id, normalizedMatch] as const;
      })
    );
  }

  private clearOpenMatches() {
    this.matches.replace(
      [...this.matches.entries()].filter(([, match]) => match.status === 'NEW')
    );
  }

  private clearNewMatches() {
    this.matches.replace(
      [...this.matches.entries()].filter(([, match]) => match.status !== 'NEW')
    );
  }

  private replaceOpenMatches(openMatches: Match[]) {
    this.clearOpenMatches();
    this.addMatches(openMatches);
  }

  private replaceNewMatches(newMatches: Match[]) {
    this.clearNewMatches();
    this.addMatches(newMatches);
  }

  private setLastLoadedOpenAt(lastMatch: Match | undefined) {
    this.lastLoadedOpenAt.lastAt = lastMatch?.last_at ?? 0;
    this.lastLoadedOpenAt.favouriteAt = lastMatch?.favourite_at ?? 0;
  }

  private setLastLoadedNewAt(lastMatch: Match | undefined) {
    this.lastLoadedNewAt = lastMatch?.last_at ?? 0;
  }

  addOpenMatches(openMatches: Match[]) {
    if (openMatches.length === 0) {
      return;
    }

    this.setLastLoadedOpenAt(openMatches[openMatches.length - 1]);
    this.addMatches(openMatches);
  }

  resetOpenMatches(openMatches: Match[]) {
    this.replaceOpenMatches(openMatches);
    this.setLastLoadedOpenAt(openMatches[openMatches.length - 1]);
    this.fetchedAllOpen = false;
  }

  addNewMatches(newMatches: Match[]) {
    if (newMatches.length === 0) {
      return;
    }

    this.setLastLoadedNewAt(newMatches[newMatches.length - 1]);
    this.addMatches(newMatches);
  }

  resetNewMatches(newMatches: Match[]) {
    this.replaceNewMatches(newMatches);
    this.setLastLoadedNewAt(newMatches[newMatches.length - 1]);
    this.fetchedAllNew = false;
  }

  addMatch(match: Match) {
    const normalizedMatch = this.normalizeMatch(match);

    this.matches.set(normalizedMatch.id, normalizedMatch);
  }

  deleteMatch(id: Match['id']) {
    this.matches.delete(id);
  }

  updateMatch(matchId: number, updater: (match: Match) => void) {
    const match = this.matches.get(matchId);
    if (!match) {
      return;
    }

    updater(match);
  }

  updateMatches(updater: (match: Match) => void) {
    this.matches.forEach(updater);
  }

  getMatchById(matchId: number): Match | undefined {
    return this.matches.get(matchId);
  }

  getMatchByUserId(userId: User['id']) {
    return [...this.matches.values()].find((match) => match.user_id === userId);
  }

  setFavourite(matchId: number, favouriteAt?: number) {
    const match = this.matches.get(matchId);

    if (!match) {
      return;
    }

    match.favourite_at = favouriteAt;
  }

  setLastOpenViewport(value: LastViewport) {
    this.lastOpenViewport = value;
  }

  getLastOpenViewport() {
    return this.lastOpenViewport;
  }

  clearLastOpenViewport() {
    this.lastOpenViewport = undefined;
  }

  setLastNewViewport(value: LastRowViewport) {
    this.lastNewViewport = value;
  }

  getLastNewViewport() {
    return this.lastNewViewport;
  }

  clearLastNewViewport() {
    this.lastNewViewport = undefined;
  }

  reset() {
    this.matches.clear();
    this.lastLoadedOpenAt = {
      lastAt: 0,
      favouriteAt: 0,
    };
    this.lastLoadedNewAt = 0;

    this.setFetchedAllOpen(false);
    this.setFetchedAllNew(false);
    this.setInitialLoaded(false);
    this.clearLastOpenViewport();
    this.clearLastNewViewport();
  }
}

export default new MatchStore();
